import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { SETTINGS_NAV_ROWS } from '../../settings-shell/navData';
import { useGmailStore } from '../../settings-gmail/store';
import type { SetChild, SetNode, SettingsNavProps } from '../../settings-shell/types';

/** The email routes carry a red dot while Gmail needs reconnecting (spec 38 §4). */
const EMAIL_ROUTE = '/settings/ai/channels/email';

/**
 * The settings column (spec 15 §2.2–2.4). 258px, white card, its own scroll.
 *
 * The look follows the Online Booking settings nav (reference image): a title
 * header, mono-uppercase section labels, and rows that show their state as a
 * tinted pill + a coloured leading icon + a trailing dot — rendered in FrontDesk's
 * own indigo palette rather than the Booking app's cream/green one. The accordion
 * groups, count badges, lock glyphs and the Gmail reconnect dot are kept.
 *
 * The boards cannot show the scroll behaviour: on an artboard the column is as
 * tall as the page. Here it fills the available height and scrolls inside
 * itself, so a 3,000px page does not give a 3,000px nav (§2.4).
 */

/** Which groups the user has opened. Persists for the session, not past a logout (§2.3). */
const sessionOpen: Record<string, boolean> = {};

function groupOwning(nodes: SetNode[], route: string): string | null {
  for (const n of nodes) {
    if (n.kind === 'group' && n.children.some((c) => isActive(c.route, route))) return n.key;
  }
  return null;
}

function isActive(rowRoute: string, activeRoute: string): boolean {
  return activeRoute === rowRoute || activeRoute.startsWith(`${rowRoute}/`);
}

export function SettingsNav({
  activeRoute,
  onNavigate,
  badges,
  lockedKeys,
  nodes = SETTINGS_NAV_ROWS,
  className,
}: SettingsNavProps) {
  const gmailExpired = useGmailStore().state === 'expired';
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const owning = groupOwning(nodes, activeRoute);
    return owning ? { ...sessionOpen, [owning]: true } : { ...sessionOpen };
  });

  // Any navigation reopens the group holding the active page — a deep link or a
  // refresh never lands with the active row hidden (§2.3).
  useEffect(() => {
    const owning = groupOwning(nodes, activeRoute);
    if (owning) {
      setOpen((prev) => (prev[owning] ? prev : { ...prev, [owning]: true }));
      sessionOpen[owning] = true;
    }
  }, [activeRoute, nodes]);

  const toggle = (key: string) => {
    setOpen((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      sessionOpen[key] = next[key] ?? false;
      return next;
    });
  };

  return (
    <nav
      aria-label="Settings"
      className={cn(
        'flex w-[258px] shrink-0 flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)]',
        className,
      )}
    >
      {/* Title header — the column's identity (reference image). */}
      <div className="shrink-0 px-5 pb-3 pt-[18px]">
        <div className="mb-1 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--text-muted)]">
          Settings
        </div>
        <h2 className="m-0 text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--text-primary)]">
          Practice <span className="text-[var(--brand-indigo)]">settings</span>
        </h2>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {nodes.map((n) => {
          if (n.kind === 'divider') {
            // A label, not a control: no hover, no focus ring, not in the tab order.
            return (
              <div
                key={n.key}
                className="px-5 pb-1 pt-4 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]"
              >
                {n.label}
              </div>
            );
          }

          if (n.kind === 'group') {
            const isOpen = open[n.key] === true;
            return (
              <div key={n.key}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => { toggle(n.key); }}
                  className={cn(
                    'mx-2 flex w-[calc(100%-16px)] items-center gap-2.5 rounded-[9px] px-3 py-[9px] text-left text-[13px] font-semibold hover:bg-[var(--surface-sunken)]',
                    isOpen ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]',
                  )}
                >
                  <Icon
                    kind={n.icon}
                    size={16}
                    stroke={1.8}
                    style={{ color: isOpen ? 'var(--brand-indigo)' : 'var(--text-muted)' }}
                  />
                  <span className="flex-1 truncate">{n.label}</span>
                  <Icon kind={isOpen ? 'up' : 'down'} size={13} stroke={2} style={{ color: 'var(--text-muted)' }} />
                </button>
                {isOpen
                  ? n.children.map((c) => (
                      <ChildRow key={c.key} child={c} activeRoute={activeRoute} onNavigate={onNavigate} redDot={gmailExpired && c.route === EMAIL_ROUTE} />
                    ))
                  : null}
              </div>
            );
          }

          const locked = n.locked === true || lockedKeys?.includes(n.key) === true;
          const count = n.badge ? badges?.[n.badge] : undefined;
          return (
            <LeafRow
              key={n.key}
              node={n}
              locked={locked}
              count={count}
              redDot={gmailExpired && n.route === EMAIL_ROUTE}
              activeRoute={activeRoute}
              onNavigate={onNavigate}
            />
          );
        })}

        <div className="h-[8px]" />
      </div>

      {/* Live config footer (reference image). The dot stays green — it is a
          status ("live"), not a brand accent. */}
      <div className="shrink-0 border-t border-[var(--border-default)] px-5 py-3">
        <div className="mb-0.5 flex items-center gap-2">
          <span className="inline-flex size-[7px] shrink-0 rounded-full bg-[var(--success)]" />
          <span className="text-[12px] font-semibold tracking-[-0.005em] text-[var(--text-primary)]">Live config</span>
        </div>
        <p className="m-0 text-[11px] leading-normal tracking-[-0.005em] text-[var(--text-muted)]">
          Changes apply to your front desk the moment you save.
        </p>
      </div>
    </nav>
  );
}

function ChildRow({
  child,
  activeRoute,
  onNavigate,
  redDot,
}: {
  child: SetChild;
  activeRoute: string;
  onNavigate: ((route: string) => void) | undefined;
  redDot?: boolean;
}) {
  const active = isActive(child.route, activeRoute);
  return (
    <a
      href={child.route}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        if (onNavigate) {
          e.preventDefault();
          onNavigate(child.route);
        }
      }}
      className={cn(
        'mx-2 my-px flex items-center gap-2 rounded-[9px] py-2 pl-[34px] pr-3 text-[13px]',
        active
          ? 'bg-[var(--brand-indigo-tint)] font-semibold text-[var(--brand-indigo)]'
          : 'font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
        {child.label}
        {redDot === true ? <span aria-label="Needs reconnecting" className="inline-block size-[6px] shrink-0 rounded-full bg-[var(--alert-red)]" /> : null}
      </span>
      {active && redDot !== true ? (
        <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-[var(--brand-indigo)]" />
      ) : null}
    </a>
  );
}

function LeafRow({
  node,
  locked,
  count,
  redDot,
  activeRoute,
  onNavigate,
}: {
  node: Extract<SetNode, { kind: 'leaf' }>;
  locked: boolean;
  count: number | undefined;
  redDot?: boolean;
  activeRoute: string;
  onNavigate: ((route: string) => void) | undefined;
}) {
  const active = isActive(node.route, activeRoute);
  // Hidden entirely at 0 — never render `0` (§2.3).
  const showBadge = typeof count === 'number' && count > 0;
  // The trailing dot is the reference's active marker — but only when nothing
  // more informative (a badge, a lock, a reconnect dot) occupies that slot.
  const showDot = active && !showBadge && !locked && redDot !== true;
  return (
    <a
      href={node.route}
      aria-current={active ? 'page' : undefined}
      aria-label={locked ? `${node.label}, not included in this plan` : undefined}
      onClick={(e) => {
        if (onNavigate) {
          e.preventDefault();
          onNavigate(node.route);
        }
      }}
      className={cn(
        'mx-2 my-px flex items-center gap-2.5 rounded-[9px] py-[9px] px-3 text-[13px]',
        active
          ? 'bg-[var(--brand-indigo-tint)] font-semibold text-[var(--brand-indigo)]'
          : locked
            ? 'font-medium text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]'
            : 'font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      <Icon
        kind={node.icon}
        size={16}
        stroke={1.8}
        style={{ color: active ? 'var(--brand-indigo)' : 'var(--text-muted)' }}
      />
      <span className="flex-1 truncate">{node.label}</span>
      {redDot === true ? <span aria-label="Needs reconnecting" className="size-[6px] shrink-0 rounded-full bg-[var(--alert-red)]" /> : null}
      {showBadge ? (
        <span
          className={cn(
            'rounded-full px-1.5 py-px font-[var(--font-mono)] text-[10.5px] font-bold',
            active ? 'bg-[var(--surface-card)] text-[var(--brand-indigo)]' : 'bg-[var(--surface-sunken)] text-[var(--text-muted)]',
          )}
        >
          {count}
        </span>
      ) : null}
      {locked ? <Icon kind="lock" size={12} stroke={2} style={{ color: 'var(--text-muted)' }} /> : null}
      {showDot ? <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-[var(--brand-indigo)]" /> : null}
    </a>
  );
}
